import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

export interface DialogOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface DialogState {
  kind: 'confirm' | 'alert';
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

/**
 * Hook que reemplaza `window.confirm` / `window.alert` por un modal coherente
 * con el resto del admin. Devuelve Promises para que encajen con flujos async:
 *
 *     const { confirm, alert, element } = useDialog();
 *     if (!await confirm({ message: '...' })) return;
 *     await doStuff();
 *     await alert({ message: 'Error', danger: true });
 *
 * Render `{element}` una vez al final del componente.
 */
export function useDialog() {
  const [state, setState] = useState<DialogState | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(ok);
  }, []);

  const confirm = useCallback((opts: DialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ kind: 'confirm', ...opts });
    });
  }, []);

  const alert = useCallback((opts: DialogOptions): Promise<void> => {
    return new Promise((resolve) => {
      resolverRef.current = () => resolve();
      setState({
        kind: 'alert',
        confirmLabel: 'Cerrar',
        ...opts,
      });
    });
  }, []);

  const element = state ? (
    <ConfirmDialog
      open
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      danger={state.danger}
      hideCancel={state.kind === 'alert'}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  ) : null;

  return { confirm, alert, element };
}
